import type { Permission } from '@ea/shared';
import { lazy as reactLazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useRouteError, type RouteObject } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { ErrorState, Loading } from './components/ui';
import { homePath, useAuth } from './lib/auth';
import { isStaleBuildError, reloadForNewBuild } from './lib/stale-build';

/** Lazy page that reloads once onto the new build when its file is gone after a deploy. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazy = <T extends ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  reactLazy<T>(() => load().catch((e: unknown) => (isStaleBuildError(e) && reloadForNewBuild() ? new Promise<{ default: T }>(() => {}) : Promise.reject(e))));

/** Shown instead of the router's developer error screen. */
function RouteError() {
  const error = useRouteError();
  if (isStaleBuildError(error) && reloadForNewBuild()) return <Loading />;
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg">
      <ErrorState error={isStaleBuildError(error) ? new Error('A new version of the portal is available. Reload the page to continue.') : error} retry={() => window.location.reload()} />
    </div>
  );
}

const P = {
  Login: lazy(() => import('./pages/auth/Login')),
  AcceptInvite: lazy(() => import('./pages/auth/AcceptInvite')),
  QuoteApproval: lazy(() => import('./pages/public/QuoteApproval')),
  PayInvoice: lazy(() => import('./pages/public/PayInvoice')),
  SignLease: lazy(() => import('./pages/public/SignLease')),
  SignContract: lazy(() => import('./pages/public/SignContract')),
  InvoiceList: lazy(() => import('./pages/finance/invoices/InvoiceList')),
  InvoiceForm: lazy(() => import('./pages/finance/invoices/InvoiceForm')),
  InvoiceDetail: lazy(() => import('./pages/finance/invoices/InvoiceDetail')),
  IdPackDashboard: lazy(() => import('./pages/services/idpack/IdPackDashboard')),
  IdPackWizard: lazy(() => import('./pages/services/idpack/IdPackWizard')),
  ServicesHome: lazy(() => import('./pages/services/ServicesHome')),
  EnrollmentDetail: lazy(() => import('./pages/services/EnrollmentDetail')),
  BankerQueue: lazy(() => import('./pages/services/BankerQueue')),
  GlobalTransfer: lazy(() => import('./pages/services/GlobalTransfer')),
  BankApplications: lazy(() => import('./pages/services/bank/BankApplications')),
  BankClient: lazy(() => import('./pages/services/bank/BankClient')),
  BankCallSheet: lazy(() => import('./pages/services/bank/BankCallSheet')),
  ServicesSupervision: lazy(() => import('./pages/services/Supervision')),
  ServicesReports: lazy(() => import('./pages/services/Reports')),
  CreditDashboard: lazy(() => import('./pages/services/credit/CreditDashboard')),
  CreditReview: lazy(() => import('./pages/services/credit/ReviewReport')),
  Workspace: lazy(() => import('./pages/dashboards/Workspace')),
  Management: lazy(() => import('./pages/dashboards/Management')),
  Supervisor: lazy(() => import('./pages/dashboards/Supervisor')),
  Executive: lazy(() => import('./pages/dashboards/Executive')),
  Finance: lazy(() => import('./pages/dashboards/Finance')),
  TaskList: lazy(() => import('./pages/tasks/TaskList')),
  TaskDetail: lazy(() => import('./pages/tasks/TaskDetail')),
  ShipmentList: lazy(() => import('./pages/shipments/ShipmentList')),
  ShipmentNew: lazy(() => import('./pages/shipments/ShipmentNew')),
  ShipmentDetail: lazy(() => import('./pages/shipments/ShipmentDetail')),
  LiveTracking: lazy(() => import('./pages/shipments/LiveTracking')),
  LabelList: lazy(() => import('./pages/labels/LabelList')),
  FinanceOverview: lazy(() => import('./pages/finance/FinanceOverview')),
  Ledger: lazy(() => import('./pages/finance/Ledger')),
  Payroll: lazy(() => import('./pages/finance/Payroll')),
  Credit: lazy(() => import('./pages/finance/Credit')),
  FinanceReports: lazy(() => import('./pages/finance/Reports')),
  OpsPayments: lazy(() => import('./pages/finance/OpsPayments')),
  OpsPaymentsMine: lazy(() => import('./pages/finance/OpsPaymentsMine')),
  OpsPayees: lazy(() => import('./pages/finance/OpsPayees')),
  PayoutList: lazy(() => import('./pages/payouts/PayoutList')),
  PayoutDetail: lazy(() => import('./pages/payouts/PayoutDetail')),
  FinalApprovals: lazy(() => import('./pages/payouts/FinalApprovals')),
  PayrollReview: lazy(() => import('./pages/payouts/PayrollReview')),
  PayoutExceptions: lazy(() => import('./pages/payouts/PayoutExceptions')),
  TicketList: lazy(() => import('./pages/tickets/TicketList')),
  TicketDetail: lazy(() => import('./pages/tickets/TicketDetail')),
  EscalationInbox: lazy(() => import('./pages/tickets/EscalationInbox')),
  BankCallList: lazy(() => import('./pages/bankcalls/BankCallList')),
  BankCallDetail: lazy(() => import('./pages/bankcalls/BankCallDetail')),
  Communications: lazy(() => import('./pages/communications/Communications')),
  WhatsAppInbox: lazy(() => import('./pages/whatsapp/WhatsAppInbox')),
  Checklist: lazy(() => import('./pages/checklist/Checklist')),
  ClientList: lazy(() => import('./pages/clients/ClientList')),
  ClientDetail: lazy(() => import('./pages/clients/ClientDetail')),
  ClientDuplicates: lazy(() => import('./pages/clients/ClientDuplicates')),
  RepresentativeList: lazy(() => import('./pages/representatives/RepresentativeList')),
  RepresentativeDetail: lazy(() => import('./pages/representatives/RepresentativeDetail')),
  AddressList: lazy(() => import('./pages/addresses/AddressList')),
  AddressDetail: lazy(() => import('./pages/addresses/AddressDetail')),
  ContractList: lazy(() => import('./pages/contracts/ContractList')),
  ContractDetail: lazy(() => import('./pages/contracts/ContractDetail')),
  LeaseList: lazy(() => import('./pages/leases/LeaseList')),
  LeaseDetail: lazy(() => import('./pages/leases/LeaseDetail')),
  AdminLeaseTemplates: lazy(() => import('./pages/admin/LeaseTemplates')),
  AdminContractTemplates: lazy(() => import('./pages/admin/ContractTemplates')),
  PaymentList: lazy(() => import('./pages/payments/PaymentList')),
  PaymentDetail: lazy(() => import('./pages/payments/PaymentDetail')),
  ReportList: lazy(() => import('./pages/reports/ReportList')),
  ReportDetail: lazy(() => import('./pages/reports/ReportDetail')),
  Employees: lazy(() => import('./pages/employees/Employees')),
  Analytics: lazy(() => import('./pages/analytics/Analytics')),
  AdminUsers: lazy(() => import('./pages/admin/Users')),
  AdminAnnouncements: lazy(() => import('./pages/admin/Announcements')),
  AdminSettings: lazy(() => import('./pages/admin/Settings')),
  AdminIntegrations: lazy(() => import('./pages/admin/Integrations')),
  AdminAudit: lazy(() => import('./pages/admin/Audit')),
  AdminDeliveryLog: lazy(() => import('./pages/admin/DeliveryLog')),
  Notifications: lazy(() => import('./pages/misc/Notifications')),
  Profile: lazy(() => import('./pages/misc/Profile')),
  NotFound: lazy(() => import('./pages/misc/NotFound')),
  RepOps: lazy(() => import('./pages/repops/RepOps')),
  RepOpsMail: lazy(() => import('./pages/repops/RepOpsMail')),
  RepOpsConversation: lazy(() => import('./pages/repops/RepOpsConversation')),
};

/** Client portal (My Services / My Progress, documents, inquiries). */
const C = {
  Shell: lazy(() => import('./portal/PortalShell')),
  Home: lazy(() => import('./portal/pages/PortalHome')),
  Services: lazy(() => import('./portal/pages/PortalServices')),
  ServiceDetail: lazy(() => import('./portal/pages/PortalServiceDetail')),
  Documents: lazy(() => import('./portal/pages/PortalDocuments')),
  Messages: lazy(() => import('./portal/pages/PortalMessages')),
  Thread: lazy(() => import('./portal/pages/PortalThread')),
  Notifications: lazy(() => import('./portal/pages/PortalNotifications')),
  Profile: lazy(() => import('./portal/pages/PortalProfile')),
  Credit: lazy(() => import('./portal/pages/PortalCredit')),
};

/** Address Representative Portal (mobile PWA). */
const R = {
  Shell: lazy(() => import('./rep/RepShell')),
  Home: lazy(() => import('./rep/pages/RepHome')),
  Scan: lazy(() => import('./rep/pages/RepScan')),
  MailList: lazy(() => import('./rep/pages/MailList')),
  MailDetail: lazy(() => import('./rep/pages/MailDetail')),
  Labels: lazy(() => import('./rep/pages/Labels')),
  LabelDetail: lazy(() => import('./rep/pages/LabelDetail')),
  Dropoffs: lazy(() => import('./rep/pages/Dropoffs')),
  DropoffNew: lazy(() => import('./rep/pages/DropoffNew')),
  DropoffDetail: lazy(() => import('./rep/pages/DropoffDetail')),
  Expenses: lazy(() => import('./rep/pages/Expenses')),
  ExpenseNew: lazy(() => import('./rep/pages/ExpenseNew')),
  ExpenseDetail: lazy(() => import('./rep/pages/ExpenseDetail')),
  Tasks: lazy(() => import('./rep/pages/Tasks')),
  Settlements: lazy(() => import('./rep/pages/Settlements')),
  SettlementDetail: lazy(() => import('./rep/pages/SettlementDetail')),
  Payments: lazy(() => import('./rep/pages/Payments')),
  Messages: lazy(() => import('./rep/pages/Messages')),
  Conversation: lazy(() => import('./rep/pages/Conversation')),
  Tickets: lazy(() => import('./rep/pages/Tickets')),
  TicketNew: lazy(() => import('./rep/pages/TicketNew')),
  TicketDetail: lazy(() => import('./rep/pages/TicketDetail')),
  Addresses: lazy(() => import('./rep/pages/Addresses')),
  Documents: lazy(() => import('./rep/pages/Documents')),
  Closeout: lazy(() => import('./rep/pages/Closeout')),
  Performance: lazy(() => import('./rep/pages/Performance')),
  Notifications: lazy(() => import('./rep/pages/Notifications')),
  NotificationSettings: lazy(() => import('./rep/pages/NotificationSettings')),
  More: lazy(() => import('./rep/pages/More')),
  Profile: lazy(() => import('./rep/pages/Profile')),
};

function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading label="Signing in…" />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/** UX guard only — every API also enforces permissions on the server. */
function Guard({ perm, any, children }: { perm?: Permission; any?: Permission[]; children: ReactNode }) {
  const { can } = useAuth();
  const ok = perm ? can(perm) : any ? any.some(can) : true;
  return ok ? <>{children}</> : <ErrorState error={new Error('You do not have access to this page.')} />;
}

/** Delivery log / WhatsApp health: system administrators and operations supervisors (role based, as on the server). */
function DeliveryLogGuard({ children }: { children: ReactNode }) {
  const { hasRole } = useAuth();
  return hasRole('system_administrator') || hasRole('operations_supervisor') ? <>{children}</> : <ErrorState error={new Error('You do not have access to this page.')} />;
}

function Home() {
  const { me } = useAuth();
  return <Navigate to={homePath(me)} replace />;
}

const s = (el: ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;
const g = (perm: Permission, el: ReactNode) => s(<Guard perm={perm}>{el}</Guard>);

const REP_STAFF: Permission[] = ['rep.ops', 'rep.manage', 'rep.settlement_verify', 'rep.settlement_approve', 'rep.settlement_pay'];
/** Representative app pages. Anyone else who lands here (e.g. staff opening the installed phone app) goes to their own home. */
function RepGuard({ children }: { children: ReactNode }) {
  const { me, can } = useAuth();
  return can('rep.portal') ? <>{children}</> : <Navigate to={homePath(me)} replace />;
}
const r = (el: ReactNode) => s(<RepGuard>{el}</RepGuard>);
const c = (el: ReactNode) => s(<Guard perm="client_portal.use">{el}</Guard>);

const routes: RouteObject[] = [
  { path: '/login', element: s(<P.Login />) },
  { path: '/invite/:token', element: s(<P.AcceptInvite />) },
  { path: '/q/:token', element: s(<P.QuoteApproval />) },
  { path: '/pay/:token', element: s(<P.PayInvoice />) },
  { path: '/sign/contract/:token', element: s(<P.SignContract />) },
  { path: '/sign/:token', element: s(<P.SignLease />) },
  {
    path: '/portal',
    element: <RequireAuth>{s(<C.Shell />)}</RequireAuth>,
    children: [
      { index: true, element: c(<C.Home />) },
      { path: 'services', element: c(<C.Services />) },
      { path: 'services/:id', element: c(<C.ServiceDetail />) },
      { path: 'credit', element: c(<C.Credit />) },
      { path: 'documents', element: c(<C.Documents />) },
      { path: 'messages', element: c(<C.Messages />) },
      { path: 'messages/:id', element: c(<C.Thread />) },
      { path: 'notifications', element: c(<C.Notifications />) },
      { path: 'profile', element: c(<C.Profile />) },
    ],
  },
  {
    path: '/rep',
    element: <RequireAuth>{s(<R.Shell />)}</RequireAuth>,
    children: [
      { index: true, element: r(<R.Home />) },
      { path: 'scan', element: r(<R.Scan />) },
      { path: 'mail', element: r(<R.MailList />) },
      { path: 'mail/:id', element: r(<R.MailDetail />) },
      { path: 'labels', element: r(<R.Labels />) },
      { path: 'labels/:id', element: r(<R.LabelDetail />) },
      { path: 'dropoffs', element: r(<R.Dropoffs />) },
      { path: 'dropoffs/new', element: r(<R.DropoffNew />) },
      { path: 'dropoffs/:id', element: r(<R.DropoffDetail />) },
      { path: 'expenses', element: r(<R.Expenses />) },
      { path: 'expenses/new', element: r(<R.ExpenseNew />) },
      { path: 'expenses/:id', element: r(<R.ExpenseDetail />) },
      { path: 'tasks', element: r(<R.Tasks />) },
      { path: 'settlements', element: r(<R.Settlements />) },
      { path: 'settlements/:id', element: r(<R.SettlementDetail />) },
      { path: 'payments', element: r(<R.Payments />) },
      { path: 'messages', element: r(<R.Messages />) },
      { path: 'messages/:id', element: r(<R.Conversation />) },
      { path: 'tickets', element: r(<R.Tickets />) },
      { path: 'tickets/new', element: r(<R.TicketNew />) },
      { path: 'tickets/:id', element: r(<R.TicketDetail />) },
      { path: 'addresses', element: r(<R.Addresses />) },
      { path: 'documents', element: r(<R.Documents />) },
      { path: 'closeout', element: r(<R.Closeout />) },
      { path: 'performance', element: r(<R.Performance />) },
      { path: 'notifications', element: r(<R.Notifications />) },
      { path: 'notifications/settings', element: r(<R.NotificationSettings />) },
      { path: 'more', element: r(<R.More />) },
      { path: 'profile', element: r(<R.Profile />) },
    ],
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'workspace', element: g('dashboard.operations', <P.Workspace />) },
      { path: 'management', element: g('dashboard.shipping_management', <P.Management />) },
      { path: 'supervisor', element: g('dashboard.supervisor', <P.Supervisor />) },
      { path: 'executive', element: g('dashboard.executive', <P.Executive />) },
      { path: 'payment-processing', element: g('dashboard.finance', <P.Finance />) },
      { path: 'tasks', element: g('task.read', <P.TaskList />) },
      { path: 'tasks/:id', element: g('task.read', <P.TaskDetail />) },
      { path: 'shipments', element: g('shipment.read', <P.ShipmentList />) },
      { path: 'shipments/new', element: g('shipment.create', <P.ShipmentNew />) },
      { path: 'shipments/:id', element: g('shipment.read', <P.ShipmentDetail />) },
      { path: 'tracking', element: g('shipment.read', <P.LiveTracking />) },
      { path: 'labels', element: g('shipment.read', <P.LabelList />) },
      { path: 'services/idpack', element: s(<Guard any={['idpack.request', 'idpack.submit', 'services.read', 'services.manage']}><P.IdPackDashboard /></Guard>) },
      { path: 'services/idpack/:enrollmentId', element: s(<Guard any={['idpack.request', 'idpack.submit', 'services.read', 'services.manage']}><P.IdPackWizard /></Guard>) },
      { path: 'services', element: s(<Guard any={['services.read', 'services.manage']}><P.ServicesHome /></Guard>) },
      { path: 'services/global-transfer', element: s(<Guard any={['services.read', 'services.manage', 'services.banker', 'services.global_transfer']}><P.GlobalTransfer /></Guard>) },
      { path: 'services/banker', element: s(<Guard any={['services.banker', 'services.manage']}><P.BankerQueue /></Guard>) },
      { path: 'services/supervisor', element: s(<Guard any={['services.read', 'services.manage']}><P.ServicesSupervision /></Guard>) },
      { path: 'services/reports', element: s(<Guard any={['services.read', 'services.manage']}><P.ServicesReports /></Guard>) },
      { path: 'services/credit/bank-applications', element: s(<Guard any={['services.read', 'services.manage', 'services.bank_applications']}><P.BankApplications /></Guard>) },
      { path: 'services/credit/bank-applications/client/:clientId', element: s(<Guard any={['services.read', 'services.manage', 'services.bank_applications']}><P.BankClient /></Guard>) },
      { path: 'services/credit/bank-applications/:id/call-sheet', element: s(<Guard any={['services.read', 'services.manage', 'services.bank_applications']}><P.BankCallSheet /></Guard>) },
      { path: 'services/credit', element: g('credit.read', <P.CreditDashboard />) },
      { path: 'services/credit/reports/:id', element: g('credit.read', <P.CreditReview />) },
      { path: 'services/:id', element: s(<Guard any={['services.read', 'services.manage', 'services.banker', 'services.global_transfer']}><P.EnrollmentDetail /></Guard>) },
      { path: 'finance', element: g('finance.read', <P.FinanceOverview />) },
      { path: 'finance/ledger', element: g('finance.read', <P.Ledger />) },
      { path: 'finance/payroll', element: g('finance.read', <P.Payroll />) },
      { path: 'finance/credit', element: g('finance.read', <P.Credit />) },
      { path: 'finance/reports', element: g('finance.read', <P.FinanceReports />) },
      { path: 'finance/invoices', element: g('finance.read', <P.InvoiceList />) },
      { path: 'finance/invoices/new', element: g('finance.write', <P.InvoiceForm />) },
      { path: 'finance/invoices/:id', element: g('finance.read', <P.InvoiceDetail />) },
      { path: 'finance/invoices/:id/edit', element: g('finance.write', <P.InvoiceForm />) },
      { path: 'finance/ops-payments', element: s(<Guard any={['opspay.pay', 'opspay.approve']}><P.OpsPayments /></Guard>) },
      { path: 'services/payments', element: g('opspay.request', <P.OpsPaymentsMine />) },
      { path: 'services/payees', element: s(<Guard any={['opspay.request', 'opspay.approve', 'opspay.pay', 'payee.manage']}><P.OpsPayees /></Guard>) },
      // Representative Payments (one page): payout readers, and payment readers for the older requests tab.
      { path: 'payouts', element: s(<Guard any={['payout.read', 'payment.read']}><P.PayoutList /></Guard>) },
      { path: 'payouts/final-approvals', element: g('payout.final_approve', <P.FinalApprovals />) },
      { path: 'payouts/payroll-review', element: g('payout.review', <P.PayrollReview />) },
      { path: 'payouts/exceptions', element: g('payout.read', <P.PayoutExceptions />) },
      { path: 'payouts/:id', element: g('payout.read', <P.PayoutDetail />) },
      { path: 'tickets', element: g('ticket.read', <P.TicketList />) },
      { path: 'tickets/:id', element: g('ticket.read', <P.TicketDetail />) },
      { path: 'escalations', element: g('ticket.read', <P.EscalationInbox />) },
      { path: 'bank-calls', element: g('bank_call.read', <P.BankCallList />) },
      { path: 'bank-calls/:id', element: g('bank_call.read', <P.BankCallDetail />) },
      { path: 'communications', element: g('communication.read', <P.Communications />) },
      { path: 'whatsapp', element: s(<Guard any={['whatsapp.read', 'whatsapp.manage']}><P.WhatsAppInbox /></Guard>) },
      { path: 'whatsapp/:id', element: s(<Guard any={['whatsapp.read', 'whatsapp.manage']}><P.WhatsAppInbox /></Guard>) },
      { path: 'checklist', element: g('checklist.use', <P.Checklist />) },
      { path: 'clients', element: g('client.read', <P.ClientList />) },
      { path: 'clients/duplicates', element: g('client.delete', <P.ClientDuplicates />) },
      { path: 'clients/:id', element: g('client.read', <P.ClientDetail />) },
      { path: 'representatives', element: g('representative.read', <P.RepresentativeList />) },
      { path: 'representatives/:id', element: g('representative.read', <P.RepresentativeDetail />) },
      { path: 'addresses', element: g('address.read', <P.AddressList />) },
      { path: 'addresses/:id', element: g('address.read', <P.AddressDetail />) },
      { path: 'contracts', element: g('contract.read', <P.ContractList />) },
      { path: 'contracts/:id', element: g('contract.read', <P.ContractDetail />) },
      { path: 'leases', element: g('lease.read', <P.LeaseList />) },
      { path: 'leases/:id', element: g('lease.read', <P.LeaseDetail />) },
      { path: 'payments', element: g('payment.read', <P.PaymentList />) },
      { path: 'payments/:id', element: g('payment.read', <P.PaymentDetail />) },
      { path: 'reports', element: s(<Guard any={['report.submit', 'report.review']}><P.ReportList /></Guard>) },
      { path: 'reports/:id', element: s(<Guard any={['report.submit', 'report.review']}><P.ReportDetail /></Guard>) },
      { path: 'employees', element: g('employee.read', <P.Employees />) },
      { path: 'analytics', element: g('analytics.read', <P.Analytics />) },
      { path: 'rep-ops', element: s(<Guard any={REP_STAFF}><P.RepOps /></Guard>) },
      { path: 'rep-ops/mail/:id', element: s(<Guard any={REP_STAFF}><P.RepOpsMail /></Guard>) },
      { path: 'rep-ops/messages/:id', element: s(<Guard any={REP_STAFF}><P.RepOpsConversation /></Guard>) },
      { path: 'admin/users', element: g('admin.users', <P.AdminUsers />) },
      { path: 'admin/announcements', element: g('announcement.manage', <P.AdminAnnouncements />) },
      { path: 'admin/settings', element: g('admin.settings', <P.AdminSettings />) },
      { path: 'admin/lease-templates', element: g('lease.template', <P.AdminLeaseTemplates />) },
      { path: 'admin/contract-templates', element: g('contract.template', <P.AdminContractTemplates />) },
      { path: 'admin/integrations', element: g('admin.integrations', <P.AdminIntegrations />) },
      { path: 'admin/audit', element: g('admin.audit', <P.AdminAudit />) },
      { path: 'admin/delivery-log', element: s(<DeliveryLogGuard><P.AdminDeliveryLog /></DeliveryLogGuard>) },
      { path: 'notifications', element: s(<P.Notifications />) },
      { path: 'profile', element: s(<P.Profile />) },
      { path: '*', element: s(<P.NotFound />) },
    ],
  },
];
const router = createBrowserRouter(routes.map((r) => ({ errorElement: <RouteError />, ...r })));

export function App() {
  return <RouterProvider router={router} />;
}
